import { Dialog } from "@/components";
import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatDateTime, parseRowData, rowStatusMeta } from "../consts";

interface RowDetailDialogProps {
  row: any;
  onClose: () => void;
  onOpenUser: (userId: any) => void;
}

/** One audit row: the original Excel values next to what the import did with them. */
const RowDetailDialog = ({ row, onClose, onOpenUser }: RowDetailDialogProps) => {
  const meta = rowStatusMeta[row?.status];
  const sourceValues = parseRowData(row?.attachmentRowData);
  const messages: string[] = (row?.message ?? "").split(" | ").map((m: string) => m.trim()).filter(Boolean);

  return (
    <Dialog
      open={!!row}
      setOpen={(open: boolean) => !open && onClose()}
      title={`Row ${row?.attachmentRowNumber ?? ""}`}
      contentClassName="md:!max-w-[860px]"
      hideFooter
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[14px]">
        <div>
          <div className="font-semibold mb-2">In the file</div>
          {sourceValues.length ?
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-[420px] overflow-auto">
              {sourceValues.map(([key, value]) => (
                <div key={key} className="grid grid-cols-2 gap-2 px-3 py-2">
                  <span className="text-gray-500">{key}</span>
                  <span className="break-words">{value || <span className="text-gray-300">—</span>}</span>
                </div>
              ))}
            </div>
            : <span className="text-gray-400">No row data was stored.</span>}
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <div className="font-semibold mb-2">Outcome</div>
            <div className="flex flex-row items-center gap-2">
              <Badge className={cn("rounded-full", meta?.className)}>{meta?.label ?? row?.status}</Badge>
              {row?.hasWarning ? <Badge className="rounded-full bg-yellow-100 text-yellow-700">Imported with warning</Badge> : <></>}
            </div>
          </div>

          {messages.length ?
            <div>
              <div className="font-semibold mb-1">Details</div>
              <ul className="list-disc pl-5 space-y-1">
                {messages.map((message, i) => <li key={i}>{message}</li>)}
              </ul>
            </div>
            : <></>}

          <div className="grid grid-cols-[120px_1fr] gap-y-2">
            <span className="text-gray-500">User</span>
            <span>
              {row?.userId
                ? <button type="button" className="text-primary hover:underline" onClick={() => onOpenUser(row.userId)}>
                    {row?.userFullName || `User ${row.userId}`}
                  </button>
                : "-"}
            </span>
            <span className="text-gray-500">Property</span>
            <span>{row?.propertyName ? `${row.propertyName}${row?.propertyDisplayId ? ` (${row.propertyDisplayId})` : ""}` : "-"}</span>
            <span className="text-gray-500">Imported</span>
            <span>{formatDateTime(row?.createdAt)}{row?.createdByName ? ` by ${row.createdByName}` : ""}</span>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RowDetailDialog;
